import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import { InformationCircleIcon } from '@heroicons/react/20/solid';
import TradingViewWidget from './TradingViewWidget';
import TradingViewSymbolProfileWidget from './TradingViewSymbolProfileWidget';
import DisclaimerFooter from './DisclaimerFooter';
import { signalDescriptions } from '../constants/signalDescriptions';
import { formatLocalDate } from '../utils/dates';

export default function ETFPage() {
  const { ticker } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { returnTo } = location.state || {};
  const [etfData, setEtfData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTooltip, setActiveTooltip] = useState(null);

  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchETFData = async () => {
      if (!ticker) return;
      
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/etf/${ticker}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setEtfData(response.data);
        setError(null);
      } catch (error) {
        console.error('Error fetching ETF data:', error);
        setError(error.response?.data?.error || 'Failed to load ETF data');
        if (error.response?.data?.similar) {
          console.log('Similar ETFs:', error.response.data.similar);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchETFData();
  }, [ticker]);

  const toggleTooltip = (factor) => {
    if (activeTooltip === factor) {
      setActiveTooltip(null);
    } else {
      setActiveTooltip(factor);
    }
  };

  // Helper function to format score values
  const formatScore = (value) => {
    if (value === null || value === undefined || value === '') {
      return '–';
    }
    return Math.round(value);
  };

  // Helper function to get progress bar width
  const getProgressWidth = (value) => {
    if (value === null || value === undefined || value === '') {
      return 0;
    }
    return Math.round(value);
  };

  if (loading) {
    return <div className="min-h-screen bg-bg-100 p-4">
      <div className="animate-pulse flex space-x-4">
        <div className="flex-1 space-y-6 py-1">
          <div className="h-4 bg-bg-300 rounded"></div>
          <div className="space-y-3">
            <div className="h-4 bg-bg-300 rounded"></div>
          </div>
        </div>
      </div>
    </div>;
  }

  if (error) {
    return <div className="min-h-screen bg-bg-100 p-4">
      <div className="max-w-2xl mx-auto mt-8">
        <div className="bg-bg-200 rounded-lg p-6 border border-bg-300">
          <h2 className="text-xl font-semibold text-text-100 mb-2">ETF Not Found</h2>
          <p className="text-text-300 mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center px-4 py-2 bg-primary-100 text-white rounded-md hover:bg-primary-200 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    </div>;
  }

  if (!etfData) {
    return <div className="min-h-screen bg-bg-100 p-4">
      <div className="text-center text-text-200">No data available</div>
    </div>;
  }

  // Get latest signal
  const latestSignal = Math.round(etfData?.time_series[0]?.signal) || 0;

  // ETF factors (traditional quant + alternative data from underlying holdings)
  const etfFactors = ['value', 'momentum', 'quality', 'lowvol', 'earnings', 'transcripts', 'innovation'];

  return (
    <div className="min-h-screen bg-bg-100">
      <div className="mt-5 px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => {
            if (returnTo) {
              navigate(returnTo.path, { state: { activeTab: returnTo.tab } });
            } else {
              navigate(-1);
            }
          }}
          className="mb-4 flex items-center text-sm text-text-200 hover:text-text-100"
        >
          <svg 
            className="mr-2 h-4 w-4" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M10 19l-7-7m0 0l7-7m-7 7h18" 
            />
          </svg>
          Back
        </button>

        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold leading-6 text-text-100">{ticker}</h1>
            <p className="mt-2 text-sm text-text-200">Exchange-Traded Fund</p>
            {etfData.etf_info?.as_of_date && (
              <p className="mt-1 text-sm text-text-200">
                Last Updated: {formatLocalDate(etfData.etf_info.as_of_date, { month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
            )}
          </div>
        </div>

        {/* Signal Score Summary */}
        <div className="mt-6">
          <div className="flex items-center mb-5">
            <p className="bg-primary-300 text-white text-sm font-semibold inline-flex items-center p-1.5 rounded-sm">
              {latestSignal}
            </p>
            <p className="ms-2 font-medium text-text-100">
              Signal Score
            </p>
            <span className="w-1 h-1 mx-2 bg-bg-300 rounded-full"></span>
            <p className="text-sm font-medium text-text-200">
              As of {formatLocalDate(etfData?.as_of_date, { month: 'numeric', day: 'numeric', year: 'numeric' })}
            </p>
          </div>

          {/* Signal Calculation Description */}
          <div className="mt-4 relative">
            <div className="bg-gradient-to-r from-blue-600/20 via-blue-500/15 to-cyan-500/20 rounded-lg border border-blue-400/30 p-4 backdrop-blur-sm">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <InformationCircleIcon className="h-5 w-5 text-blue-300" />
                </div>
                <p className="text-sm text-white leading-relaxed">
                  ETF signal scores combine traditional quantitative factors (Value, Momentum, Quality, Low Volatility) 
                  with alternative data signals (Earnings Quality, Transcripts Sentiment, Innovation Capacity) 
                  averaged across the fund's underlying holdings, providing a comprehensive view of factor exposures.
                </p>
              </div>
            </div>
          </div>

          {/* Component Scores - Show all factors including alternative data */}
          <div className="mt-6 bg-bg-200/50 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-text-100 mb-6">Factor Exposures</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {etfFactors.map((factor) => (
                <dl key={factor} className="relative">
                  <dt className="text-sm font-medium text-text-200 capitalize mb-2 flex items-center">
                    {factor}
                    <button
                      type="button"
                      className="ml-1.5 text-text-300 hover:text-text-100 focus:outline-none"
                      onClick={() => toggleTooltip(factor)}
                      aria-label={`Information about ${factor}`}
                    >
                      <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                    {activeTooltip === factor && (
                      <div className="absolute z-10 bg-bg-300 p-2 rounded-md shadow-lg text-xs text-text-100 max-w-xs left-0 top-6">
                        {signalDescriptions[factor]}
                      </div>
                    )}
                  </dt>
                  <dd className="flex items-center">
                    <div className="w-full bg-bg-300 rounded h-2.5 me-2">
                      <div 
                        className="bg-primary-100 h-2.5 rounded" 
                        style={{ width: `${getProgressWidth(etfData?.component_scores?.[`${factor}_score`])}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-text-200 min-w-[3rem] text-right">
                      {formatScore(etfData?.component_scores?.[`${factor}_score`])}
                    </span>
                  </dd>
                </dl>
              ))}
            </div>
          </div>

          {/* Full-width TradingView Symbol Profile Widget */}
          <div className="mt-6 bg-bg-200/50 rounded-lg p-4 h-[350px]">
            <TradingViewSymbolProfileWidget key={`profile-${ticker}`} ticker={ticker} />
          </div>

          {/* TradingView Chart - Full width for ETF */}
          <div className="mt-6 bg-bg-200/50 rounded-lg p-4 h-[400px]">
            <TradingViewWidget key={`chart-${ticker}`} ticker={ticker} />
          </div>

          {/* Signal History Chart */}
          <div className="mt-6 bg-bg-200/50 rounded-lg p-4">
            <h2 className="text-xl font-semibold text-text-100 mb-4">Signal History</h2>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart 
                data={[...(etfData?.time_series || [])].reverse()}
                margin={{ top: 20, right: 30, left: 50, bottom: 20 }}
              >
                <XAxis 
                  dataKey="date"
                  tickFormatter={(date) => formatLocalDate(date, { month: 'numeric', day: 'numeric', year: 'numeric' })}
                  tick={{ fill: 'white' }}
                  height={40}
                  padding={{ left: 16, right: 16 }}
                  tickMargin={8}
                />
                <YAxis
                  domain={[0, 100]}
                  tickCount={5}
                  tick={{ fill: 'white' }}
                  tickFormatter={(tick) => `${tick}`}
                  label={{ 
                    value: 'Signal Score', 
                    angle: -90, 
                    position: 'insideLeft',
                    offset: -5,
                    style: { fill: 'white', textAnchor: 'middle' } 
                  }}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#292a37', border: 'none' }}
                  labelStyle={{ color: '#fff' }}
                  itemStyle={{ color: '#fff' }}
                  formatter={(value) => [`${Math.round(value)}`, 'Signal']}
                  labelFormatter={(date) => formatLocalDate(date, { month: 'numeric', day: 'numeric', year: 'numeric' })}
                />
                <Bar dataKey="signal" fill="#2E5BFF" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Signal History Table */}
          <div className="mt-8">
            <h3 className="text-xl font-semibold leading-6 text-text-100 mb-4">Historical Signals</h3>
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg bg-bg-200">
              <div className="m-2">
                <div className="max-h-[400px] overflow-y-auto">
                  <table className="min-w-full divide-y divide-bg-300">
                    <thead className="bg-bg-200 sticky top-0 z-10">
                      <tr>
                        <th scope="col" className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-text-100">Date</th>
                        <th scope="col" className="whitespace-nowrap px-2 py-3.5 text-right text-sm font-semibold text-text-100">Signal Score</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-bg-300 bg-bg-200">
                      {(etfData?.time_series || []).map((point) => (
                        <tr key={point.date}>
                          <td className="whitespace-nowrap px-2 py-2 text-sm text-text-200 text-left">
                            {formatLocalDate(point.date, { month: 'numeric', day: 'numeric', year: 'numeric' })}
                          </td>
                          <td className="whitespace-nowrap px-2 py-2 text-sm text-text-200 text-right">
                            {Math.round(point.signal)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <br />
        </div>
      </div>
      <br />
      <DisclaimerFooter small />
    </div>
  );
}

